import authService from '@/services/auth.service';
import { store } from '@/store/store';
import { saveOfflineRequest } from '@/utils/offlineQueue';
import ToastService from '@/utils/toast';
import { ApiErrorResponse, create } from 'apisauce';

let isRefreshing = false;
let waitingQueue: any[] = [];

export const api = create({
  baseURL: process.env.REACT_APP_BASE_URL + '/api',
  headers: { Accept: 'application/json' },
});

const processQueue = (error: ApiErrorResponse<any> | null, token = null) => {
  waitingQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  isRefreshing = false;
  waitingQueue = [];
};

api.axiosInstance.interceptors.request.use(
  async (config: any) => {
    const isOnline = store.getState().network.isOnline;
    if (!isOnline) {
      console.warn("[Offline Mode] Saving request for retry:", config.url);

      if (config.method !== 'get'){
        await saveOfflineRequest({
          url: config.url,
          method: config.method,
          data: config.data,
          headers: config.headers,
        });

        //Only show toast other then get requests
        ToastService.error("No internet. Request saved and will sync when online.");
        return Promise.reject(new Error("No internet connection. Request saved for later."));
      }
    }

    const accessToken = localStorage.getItem('atToken') || '';
    if (accessToken !== '') {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },

  (err) => Promise.reject(err)
);

api.axiosInstance.interceptors.response.use(
  async (response: any) => {
    if (response.data?.statusCode === 400 || response.data?.message) {
      return Promise.reject({
        response: {
          data: response.data,
          status: response.data.statusCode
        }
      });
    }
    return response;
  },

  async (err) => {
    const originalRequest = err.config;
    
    if ((!navigator.onLine || err.response?.status === 503) && originalRequest.method === "get") {
      ToastService.error("Unable to retrieve data. Please check your internet connection.");
      return Promise.resolve({ data: { error: "Offline Mode: No cached data available." } });
    }

    if (err.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise(function (resolve, reject) {
          waitingQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers['Authorization'] = 'Bearer ' + token;
            return api.axiosInstance(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }
      originalRequest._retry = true;
      isRefreshing = true;

      const res = await authService.refreshToken();
      if (res.data?.token) {
        api.axiosInstance.defaults.headers.common['Authorization'] =
          'Bearer ' + res.data?.token;
        processQueue(null, res.data?.accessToken);
        return api.axiosInstance(originalRequest);
      } else {
        isRefreshing = false;
        return Promise.reject(err);
      }
    }

    // Properly reject errors instead of returning them
    switch (err.response?.status) {
      case 400:
        console.error('Bad Request:', err.response.data);
        return Promise.reject(err);

      case 401:
        console.error('Unauthorized:', err.response.data);
        isRefreshing = false;
        return Promise.reject(err);

      case 404:
        console.error('Not Found:', err.response.data);
        return Promise.reject(err);

      case 500:
        console.error('Server Error:', err.response.data);
        return Promise.reject(err);

      default:
        console.error('Unhandled Error:', err);
        return Promise.reject(err);
    }
  }
);